import { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Upload, File, Image, Music, Video, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function UploadPage() {
  const { user } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploaded, setUploaded] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const getIcon = (type) => {
    if (type?.startsWith('image')) return Image;
    if (type?.startsWith('video')) return Video;
    if (type?.startsWith('audio')) return Music;
    return File;
  };

  const handleUpload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    setProgress(0);
    const results = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 10MB)`);
        continue;
      }
      const formData = new FormData();
      formData.append('file', file);
      try {
        const res = await api.post('/files/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e) => {
            const total = ((i / files.length) + (e.loaded / e.total / files.length)) * 100;
            setProgress(Math.round(total));
          }
        });
        results.push(res.data);
      } catch {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    setUploaded(prev => [...results, ...prev]);
    setUploading(false);
    setProgress(100);
    if (results.length > 0) toast.success(`${results.length} file(s) uploaded`);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleUpload(e.dataTransfer.files);
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  return (
    <div className="max-w-2xl mx-auto animate-in" data-testid="upload-page">
      <h1 className="font-serif text-2xl md:text-3xl mb-8">Upload</h1>

      {/* Drop Zone */}
      <div
        data-testid="upload-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileRef.current?.click()}
        className={`border-2 border-dashed transition-colors duration-300 cursor-pointer p-12 text-center ${
          dragOver ? 'border-gold bg-gold/5' : 'border-border/60 hover:border-gold/50'
        }`}
      >
        <input ref={fileRef} type="file" multiple className="hidden" onChange={e => handleUpload(e.target.files)} data-testid="upload-file-input" />
        <Upload size={32} className={`mx-auto mb-4 ${dragOver ? 'text-gold' : 'text-muted-foreground/40'}`} />
        <p className="text-sm font-medium mb-1">Drop files here or click to browse</p>
        <p className="text-xs text-muted-foreground">Images, videos, audio, documents - Max 10MB each</p>
      </div>

      {/* Upload Progress */}
      {uploading && (
        <div className="mt-6">
          <Progress value={progress} className="h-1 rounded-none" />
          <p className="text-xs text-muted-foreground mt-2 text-center">{progress}% uploaded</p>
        </div>
      )}

      {/* Uploaded Files */}
      {uploaded.length > 0 && (
        <div className="mt-8">
          <h2 className="font-serif text-lg mb-4">Uploaded Files</h2>
          <div className="space-y-2">
            {uploaded.map(f => {
              const Icon = getIcon(f.file_type);
              return (
                <div key={f.id} className="flex items-center gap-3 border border-border/40 p-3 hover:border-gold/30 transition-colors" data-testid={`uploaded-file-${f.id}`}>
                  <Icon size={18} className="text-gold shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{f.filename}</p>
                    <p className="text-[10px] text-muted-foreground">{formatSize(f.file_size)} &middot; {f.file_type}</p>
                  </div>
                  <CheckCircle size={16} className="text-green-600 shrink-0" />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
